/**
 * GTM Troubleshooting Mode
 * ────────────────────────
 * On-demand console diagnostics that show, for each dataLayer event:
 *   • the event name + page (path)
 *   • the dataLayer payload
 *   • which GTM tags fired (name + id, when GTM exposes them)
 *   • which triggers fired (when GTM exposes them via `gtm.triggers`)
 *
 * GTM does NOT publicly expose its tag/trigger registry from the loaded
 * container, but every tag it fires pushes a follow-up event to the
 * dataLayer of shape `{ event: 'gtm.tagFired', 'gtm.tagId': N, ... }`
 * (and similar for triggers). We hook the proxy chain to surface those.
 *
 * Activation (any one):
 *   • URL flag:        ?gtm_trace=1   (persisted to sessionStorage)
 *   • Console toggle:  __zooiGtmTrace.on() / .off() / .status()
 *   • Programmatic:    setGtmTroubleshooting(true)
 *
 * Safe in production — completely silent unless explicitly enabled.
 */

const STORAGE_KEY = "zooi_gtm_trace";
const URL_FLAG = "gtm_trace";

let installed = false;
let enabled = false;

interface TraceWindow {
  dataLayer: unknown[];
  __zooiGtmTrace?: {
    on: () => void;
    off: () => void;
    status: () => boolean;
    dump: () => unknown[];
    clear: () => void;
  };
}

function isEnabledFromEnv(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const url = new URLSearchParams(window.location.search);
    if (url.has(URL_FLAG) && url.get(URL_FLAG) !== "0") {
      sessionStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persist(state: boolean) {
  try {
    if (state) sessionStorage.setItem(STORAGE_KEY, "1");
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const traceBuffer: Array<{
  ts: number;
  event: string;
  path: string;
  payload: Record<string, unknown>;
}> = [];
const MAX_BUFFER = 300;

function logEntry(item: Record<string, unknown>) {
  if (!enabled) return;
  const eventName = typeof item.event === "string" ? item.event : "(no-event)";
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  traceBuffer.push({ ts: Date.now(), event: eventName, path, payload: { ...item } });
  if (traceBuffer.length > MAX_BUFFER) traceBuffer.splice(0, traceBuffer.length - MAX_BUFFER);

  // Color-code categories
  const isTagFired = eventName === "gtm.tagFired" || eventName === "gtm.js" || eventName === "gtm.dom" || eventName === "gtm.load";
  const isTriggerEvt = eventName.startsWith("gtm.") && !isTagFired;
  const color = isTagFired
    ? "#16a34a"   // green: tag/lifecycle
    : isTriggerEvt
      ? "#0ea5e9" // blue: gtm trigger lifecycle
      : "#a855f7"; // purple: app/business event

  // GTM exposes these on tagFired pushes when "Enable debug signal" is on
  const tagId = (item as Record<string, unknown>)["gtm.tagId"];
  const tagName = (item as Record<string, unknown>)["gtm.tagName"] ?? (item as Record<string, unknown>)["gtm.elementId"];
  const triggers = (item as Record<string, unknown>)["gtm.triggers"];

  const summary: Record<string, unknown> = { path, payload: item };
  if (tagId !== undefined) summary.tagId = tagId;
  if (tagName !== undefined) summary.tagName = tagName;
  if (triggers !== undefined) summary.triggers = triggers;

  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `%c[GTM trace] ${eventName}%c  ${path}`,
    `color:${color};font-weight:bold`,
    "color:#64748b;font-weight:normal",
  );
  // eslint-disable-next-line no-console
  console.log(summary);
  // eslint-disable-next-line no-console
  console.groupEnd();
}

/**
 * Install the troubleshooter. Idempotent. Wraps `dataLayer.push` AFTER
 * any earlier proxies (denylist, event logger), so we observe the final
 * accepted stream of events.
 *
 * IMPORTANT: call this from main.tsx AFTER `bootstrapGoogleTracking()` and
 * `installTrackingEventLogger()` so we sit on top of the existing proxy chain.
 */
export function installGtmTroubleshooter() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  enabled = isEnabledFromEnv();

  const w = window as unknown as TraceWindow;
  if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
  const dl = w.dataLayer as unknown[] & { push: (...a: unknown[]) => number };

  // Replay anything already on the dataLayer when enabled at boot
  if (enabled) {
    for (const existing of dl) {
      if (existing && typeof existing === "object") logEntry(existing as Record<string, unknown>);
    }
  }

  const originalPush = dl.push.bind(dl);
  dl.push = (...args: unknown[]) => {
    const result = originalPush(...args);
    if (enabled) {
      try {
        for (const a of args) {
          if (a && typeof a === "object") logEntry(a as Record<string, unknown>);
        }
      } catch {
        /* never break GTM */
      }
    }
    return result;
  };

  // Console handle
  w.__zooiGtmTrace = {
    on: () => {
      enabled = true;
      persist(true);
      // eslint-disable-next-line no-console
      console.log("%c[GTM trace] enabled — events will be logged per page", "color:#16a34a;font-weight:bold");
    },
    off: () => {
      enabled = false;
      persist(false);
      // eslint-disable-next-line no-console
      console.log("%c[GTM trace] disabled", "color:#64748b;font-weight:bold");
    },
    status: () => enabled,
    dump: () => traceBuffer.slice(),
    clear: () => {
      traceBuffer.length = 0;
    },
  };

  if (enabled) {
    // eslint-disable-next-line no-console
    console.log(
      "%c[GTM trace] active%c — toggle via __zooiGtmTrace.off() / .on() / .dump()",
      "color:#16a34a;font-weight:bold",
      "color:#64748b",
    );
  }
}

/** Programmatic on/off (for an admin UI toggle) */
export function setGtmTroubleshooting(state: boolean) {
  enabled = state;
  persist(state);
}

export function isGtmTroubleshootingEnabled(): boolean {
  return enabled;
}
